/*
 * Copyright (c) 2023, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {useState, useMemo, useEffect, useRef} from 'react'
import {FormattedMessage, useIntl} from 'react-intl'
import useBasketAssignments from '@app/hooks/use-basket-assignments'

import {getDefaultShipmentID} from '../../utils/basket-utils'

// Chakra Components
import {
    Box,
    Stack,
    Grid,
    GridItem,
    Container,
    useDisclosure
} from '@salesforce/retail-react-app/app/components/shared/ui'

// Project Components
import CartCta from './partials/cart-cta'
import CartApplePayCta from './partials/cart-applepay-cta'
import CartSecondaryButtonGroup from './partials/cart-secondary-button-group'
import CartSkeleton from '@salesforce/retail-react-app/app/pages/cart/partials/cart-skeleton'
import CartTitle from './partials/cart-title'
import ConfirmationModal from '../../components/confirmation-modal'
import EmptyCart from './partials/empty-cart'
import GiftWithPurchase from './partials/gift-with-purchase'
import OrderSummary from '../../components/order-summary'
import ProductItem from '../../components/product-item'
import GiftCertificateItemWithActions from './partials/gift-certificate-item-with-actions'
import ProductViewModal from '../../components/product-view-modal'
import BundleProductViewModal from '@salesforce/retail-react-app/app/components/product-view-modal/bundle'
import RecommendedProducts from '../../components/recommended-products'
import Sticky from 'react-sticky-el'
import UnavailableGiftCardModal from '@app/components/unavailable-gift-card-confirmation-modal'

// Custom Hooks
import {useStyleConfig} from '@salesforce/retail-react-app/app/components/shared/ui'
import {useToast} from '../../hooks/use-toast'
import useShipToCountry from '../../hooks/use-ship-to-country'
import {useGTM} from '@app/hooks/use-gtm'
import useSiteType from '../../hooks/use-site-type'

// OOTB HOOKS
import {useCurrency} from '@salesforce/retail-react-app/app/hooks'

// Constants
import {TOAST_MESSAGE_REMOVED_ITEM_FROM_CART} from '@salesforce/retail-react-app/app/constants'
import {
    API_ERROR_MESSAGE,
    MODELS_NO_GIFT,
    CART_PDC_PAGE_ID,
    EINSTEIN_RECOMMENDERS
} from '../../constants'
import {REMOVE_CART_ITEM_CONFIRMATION_DIALOG_CONFIG} from '@salesforce/retail-react-app/app/pages/cart/partials/cart-secondary-button-group'

// Utilities
import debounce from 'lodash/debounce'
import {useCurrentBasket} from '../../hooks/use-current-basket'
import {
    useShopperBasketsMutation,
    useShippingMethodsForShipment,
    usePage,
    useProducts
} from '@salesforce/commerce-sdk-react'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import UnavailableProductConfirmationModal from '../../components/unavailable-product-confirmation-modal'
import {getUpdateBundleChildArray} from '@salesforce/retail-react-app/app/utils/product-utils'
import {useSplioService} from '../../hooks/use-splio-service'
import {HideOnDesktop, HideOnMobile} from '@salesforce/retail-react-app/app/components/responsive'
import CartInformativeParts from './partials/cart-informative-parts'
import DisclaimerBlock from '../../components/disclaimer'
import ApproachingPromotion from './partials/approaching-promotion'
import GiftCheckbox from './partials/gift-checkbox'

// Custom Context
import {useConversionRate} from '../../custom-contexts'
import {usePageParameters} from '@app/hooks/use-page-parameters'

// utils
import {getConvertedCurrencyCode, getConvertedPrice} from '../../utils/price-utils'
import useStickyCTA from '../../hooks/use-sticky-CTA'

let updatedBasket
const DEBOUNCE_WAIT = 750
const Cart = () => {
    const styles = useStyleConfig('Cart')

    const {shipToCountry} = useShipToCountry()
    const conversionsRate = useConversionRate()
    const {currency: activeCurrency} = useCurrency()
    const {isOutlet} = useSiteType()
    const parameters = usePageParameters(CART_PDC_PAGE_ID + (isOutlet ? '-outlet' : ''), {includeCountry: true})
    const {data: cartPage} = usePage({parameters})
    const {
        data: basket,
        derivedData: {totalItems},
        isLoading,
        refetch
    } = useCurrentBasket()
    const productIds = basket?.productItems?.map(({productId}) => productId).join(',') ?? ''
    const {data: products, isLoading: isProductsLoading} = useProducts(
        {
            parameters: {
                ids: productIds,
                allImages: true,
                perPricebook: true,
                c_country: shipToCountry?.id
            }
        },
        {
            enabled: Boolean(productIds),
            select: (result) => {
                return result?.data?.reduce((result, item) => {
                    const key = item.id
                    result[key] = item
                    return result
                }, {})
            }
        }
    )

    const {data: customer} = useCurrentCustomer()
    const {isRegistered} = customer
    const {sendBasketToSplio} = useSplioService()
    const DEFAULT_SHIPMENT_ID = getDefaultShipmentID(basket)
    const assignBasket = useBasketAssignments()

    useEffect(() => {
        const performBasketAssignment = async () => {
            await assignBasket(true)
        }

        performBasketAssignment()
    }, [])

    /***************** Product Bundles ************************/
    const bundleChildVariantIds = []
    basket?.productItems?.forEach((productItem) => {
        productItem?.bundledProductItems?.forEach((childProduct) => {
            bundleChildVariantIds.push(childProduct.productId)
        })
    })

    useEffect(() => {
        /**
         * Refetches the cart data and synchronizes it with the external service.
         *
         * This asynchronous function performs two main operations:
         * 1. It refetches the current cart data by calling the `refetch` function.
         * 2. It sends the updated basket data to the Splio service by invoking
         *    the `sendBasketToSplio` function with the current basket state.
         *
         * @async
         * @function refetchData
         * @returns {Promise<void>} A promise that resolves when both operations are complete.
         */
        async function refetchData() {
            await refetch()
            await sendBasketToSplio({
                isCompleted: false,
                basket: basket,
                requestType: 'cart page'
            })
        }
        refetchData()
    }, [customer?.authType, isRegistered, isLoading])

    const {data: bundleChildProductData} = useProducts(
        {
            parameters: {
                ids: bundleChildVariantIds?.join(','),
                allImages: false,
                expand: ['availability', 'variations'],
                select: '(data.(id,inventory))',
                c_country: shipToCountry?.id
            }
        },
        {
            enabled: bundleChildVariantIds?.length > 0,
            keepPreviousData: true,
            select: (result) => {
                return result?.data?.reduce((result, item) => {
                    const key = item.id
                    result[key] = item
                    return result
                }, {})
            }
        }
    )

    // We use the `products` object to reference products by itemId instead of productId
    // Since with product bundles, even though the parent productId is the same,
    // variant selection of the bundle children can be different,
    // and require unique references to each product bundle
    const productsByItemId = useMemo(() => {
        const updateProductsByItemId = {}
        basket?.productItems?.forEach((productItem) => {
            let currentProduct = products?.[productItem?.productId]

            // calculate inventory for product bundles based on availability of children
            if (productItem?.bundledProductItems && bundleChildProductData) {
                let lowestStockLevel =
                    currentProduct?.inventory?.stockLevel ?? Number.MAX_SAFE_INTEGER
                let productWithLowestInventory = ''
                productItem?.bundledProductItems.forEach((bundleChild) => {
                    const bundleChildStockLevel =
                        bundleChildProductData?.[bundleChild.productId]?.inventory?.stockLevel ??
                        Number.MAX_SAFE_INTEGER
                    lowestStockLevel = Math.min(lowestStockLevel, bundleChildStockLevel)
                    if (lowestStockLevel === bundleChildStockLevel)
                        productWithLowestInventory = bundleChild.productName
                })

                if (currentProduct?.inventory) {
                    currentProduct = {
                        ...currentProduct,
                        inventory: {
                            ...currentProduct.inventory,
                            stockLevel: lowestStockLevel,
                            lowestStockLevelProductName: productWithLowestInventory
                        }
                    }
                }
            }
            if (currentProduct?.inventory) {
                currentProduct.inventory.stockLevel = productItem?.c_currentStockLevel
                currentProduct.inventory.ats = productItem?.c_currentStockLevel
                currentProduct.inventory.id = productItem?.inventoryId
                currentProduct.inventory.preorderable =
                    productItem?.c_currentStockLevel > 0 ? true : false
                currentProduct.inventory.orderable =
                    productItem?.c_currentStockLevel > 0 ? true : false
            }
            updateProductsByItemId[productItem.itemId] = currentProduct
        })
        return updateProductsByItemId
    }, [basket, products, bundleChildProductData])

    /*****************Basket Mutation************************/
    const updateBasket = useShopperBasketsMutation('updateBasket')
    const updateItemInBasketMutation = useShopperBasketsMutation('updateItemInBasket')
    const updateItemsInBasketMutation = useShopperBasketsMutation('updateItemsInBasket')
    const removeItemFromBasketMutation = useShopperBasketsMutation('removeItemFromBasket')
    const updateShippingMethodForShipmentsMutation = useShopperBasketsMutation(
        'updateShippingMethodForShipment'
    )
    /*****************Basket Mutation************************/
    const [selectedItem, setSelectedItem] = useState(undefined)
    const [localQuantity, setLocalQuantity] = useState({})
    const [localIsGiftItems, setLocalIsGiftItems] = useState({})
    const [isCartItemLoading, setCartItemLoading] = useState(false)
    const [productWithStockError, setProductWithStockError] = useState(null)

    const {isOpen, onOpen, onClose} = useDisclosure()
    const {formatMessage} = useIntl()
    const toast = useToast()
    const modalProps = useDisclosure()
    const {usePageLoadAndCookies, triggerViewCartEvent, triggerRemoveFromCartEvent} = useGTM()

    /******************* Shipping Methods for basket shipment *******************/
    // do this action only if the basket shipping method is not defined
    // we need to fetch the shippment methods to get the default value before we can add it to the basket
    useShippingMethodsForShipment(
        {
            parameters: {
                basketId: basket?.basketId,
                shipmentId: DEFAULT_SHIPMENT_ID
            }
        },
        {
            // only fetch if basket is has no shipping method in the first shipment
            enabled:
                !!basket?.basketId &&
                basket.shipments.length > 0 &&
                !basket.shipments[0].shippingMethod,
            onSuccess: (data) => {
                updateShippingMethodForShipmentsMutation.mutate({
                    parameters: {
                        basketId: basket?.basketId,
                        shipmentId: DEFAULT_SHIPMENT_ID
                    },
                    body: {
                        id: data.defaultShippingMethodId
                    }
                })
            }
        }
    )

    /************************* Error handling ***********************/
    const showError = () => {
        toast({
            title: formatMessage(API_ERROR_MESSAGE),
            status: 'error'
        })
    }
    /************************* Error handling ***********************/

    /***************************** Update Cart **************************/
    const handleUpdateCart = async (variant, quantity) => {
        // close the modal before handle the change
        onClose()
        // using try-catch is better than using onError callback since we have many mutation calls logic here
        try {
            setCartItemLoading(true)
            const productIds = basket.productItems.map(({productId}) => productId)

            // The user is selecting different variant, and it has not existed in basket
            if (selectedItem.id !== variant.productId && !productIds.includes(variant.productId)) {
                const item = {
                    productId: variant.productId,
                    quantity,
                    price: variant.price
                }
                return await updateItemInBasketMutation.mutateAsync({
                    parameters: {
                        basketId: basket.basketId,
                        itemId: selectedItem.itemId
                    },
                    body: item
                })
            }

            // The user is selecting different variant, and it has existed in basket
            // remove this item in the basket, change the quantity for the new selected variant in the basket
            if (selectedItem.id !== variant.productId && productIds.includes(variant.productId)) {
                await removeItemFromBasketMutation.mutateAsync({
                    parameters: {
                        basketId: basket.basketId,
                        itemId: selectedItem.itemId
                    }
                })
                const basketItem = basket.productItems.find(
                    ({productId}) => productId === variant.productId
                )
                const newQuantity = quantity + basketItem.quantity
                return await changeItemQuantity(newQuantity, basketItem)
            }

            // the user only changes quantity of the same variant
            if (selectedItem.quantity !== quantity) {
                return await changeItemQuantity(quantity, selectedItem)
            }
        } catch {
            showError()
        } finally {
            setCartItemLoading(false)
            setSelectedItem(undefined)
        }
    }

    const handleUpdateBundle = async (bundle, bundleQuantity, childProducts) => {
        // close the modal before handle the change
        onClose()

        try {
            setCartItemLoading(true)
            const itemsToBeUpdated = getUpdateBundleChildArray(bundle, childProducts)

            // We only update the parent bundle when the quantity changes
            // Since top level bundles don't have variants
            if (bundle.quantity !== bundleQuantity) {
                itemsToBeUpdated.unshift({
                    itemId: bundle.itemId,
                    productId: bundle.productId,
                    quantity: bundleQuantity
                })
            }

            if (itemsToBeUpdated.length) {
                await updateItemsInBasketMutation.mutateAsync({
                    method: 'PATCH',
                    parameters: {
                        basketId: basket.basketId
                    },
                    body: itemsToBeUpdated
                })
            }
        } catch {
            showError()
        } finally {
            setCartItemLoading(false)
            setSelectedItem(undefined)
        }
    }

    const handleIsAGiftChange = async (product, checked) => {
        try {
            const previousVal = localIsGiftItems[product.itemId]
            setLocalIsGiftItems({
                ...localIsGiftItems,
                [product.itemId]: checked
            })
            setCartItemLoading(true)
            setSelectedItem(product)
            await updateItemInBasketMutation.mutateAsync(
                {
                    parameters: {
                        basketId: basket?.basketId,
                        itemId: product.itemId
                    },
                    body: {
                        productId: product.id,
                        quantity: parseInt(product.quantity),
                        gift: checked
                    }
                },
                {
                    onSettled: () => {
                        // reset the state
                        setCartItemLoading(false)
                        setSelectedItem(undefined)
                    },
                    onSuccess: () => {
                        setLocalIsGiftItems({
                            ...localIsGiftItems,
                            [product.itemId]: checked
                        })
                    },
                    onError: () => {
                        // reset the quantity to the previous value
                        setLocalIsGiftItems({...localIsGiftItems, [product.itemId]: previousVal})
                        showError()
                    }
                }
            )
        } catch (e) {
            showError()
        } finally {
            setCartItemLoading(false)
            setSelectedItem(undefined)
        }
    }

    const handleUnavailableProducts = async (unavailableItemIds) => {
        const productItems =
            basket?.productItems?.filter((item) => unavailableItemIds?.includes(item.itemId)) || []

        await Promise.all(
            productItems.map(async (item) => {
                await handleRemoveItem(item)
            })
        )
    }

    async function fetchSplioCall() {
        await sendBasketToSplio({
            isCompleted: false,
            basket: updatedBasket,
            requestType: 'cart page'
        })
    }

    /***************************** Update Cart **************************/

    /***************************** Update quantity **************************/
    const changeItemQuantity = debounce(async (quantity, product) => {
        // This local state allows the dropdown to show the desired quantity
        // while the API call to update it is happening.
        setCartItemLoading(true)
        setSelectedItem(product)
        try {
            updatedBasket = await updateItemInBasketMutation.mutateAsync(
                {
                    parameters: {
                        basketId: basket?.basketId,
                        itemId: product.itemId
                    },
                    body: {
                        productId: product.id,
                        quantity: parseInt(quantity)
                    }
                },
                {
                    onSettled: () => {
                        // reset the state
                        setCartItemLoading(false)
                        setSelectedItem(undefined)
                    },
                    onSuccess: () => {
                        setLocalQuantity({...localQuantity, [product.itemId]: undefined})
                        setProductWithStockError(null)
                    },
                    onError: () => {
                        setProductWithStockError(product)
                    }
                }
            )
        } catch (error) {
            setProductWithStockError(product)
            console.error('Error updating item in basket:', error.message)
        }

        fetchSplioCall()
    }, DEBOUNCE_WAIT)

    const handleChangeItemQuantity = async (product, value) => {
        const stockLevel = productsByItemId?.[product.itemId]?.inventory?.stockLevel ?? 1

        // Handle removing of the items when 0 is selected.
        if (value === 0) {
            // Flush last call to keep ui in sync with data.
            changeItemQuantity.flush()

            // Set the selected item to the current product to the modal acts on it.
            setSelectedItem(product)

            // Show the modal.
            modalProps.onOpen()

            // Return false as 0 isn't valid section.
            return false
        }

        // Cancel any pending handlers.
        changeItemQuantity.cancel()

        // Allow use to selected values above the inventory.
        if (value > stockLevel || value === product.quantity) {
            // update inventory list
            changeItemQuantity(value, product)
            return true
        }

        // Take action.
        changeItemQuantity(value, product)

        return true
    }
    /***************************** Update quantity **************************/

    /***************************** Remove Item from basket **************************/
    const handleRemoveItem = async (product) => {
        setSelectedItem(product)
        setCartItemLoading(true)
        const productToBeRemoved = products[product.productId]
        updatedBasket = await removeItemFromBasketMutation.mutateAsync(
            {
                parameters: {
                    basketId: basket.basketId,
                    itemId: product.itemId
                }
            },
            {
                onSettled: () => {
                    // reset the state
                    setCartItemLoading(false)
                    setSelectedItem(undefined)
                },
                onSuccess: () => {
                    // Remove Item Here
                    triggerRemoveFromCartEvent(productToBeRemoved, product)
                    toast({
                        title: formatMessage(TOAST_MESSAGE_REMOVED_ITEM_FROM_CART, {quantity: 1}),
                        status: 'success'
                    })
                },
                onError: () => {
                    showError()
                }
            }
        )

        fetchSplioCall()
    }

    /***************************** Handle Sticky CTA *****************************/
    const topCartCTARef = useRef(null)
    const {enableStickyCTA, handleInputFocus, handleInputBlur} = useStickyCTA(topCartCTARef)

    /***************************** Handle Gift with purchase *****************************/
    const handleGiftWithPurchase = async (checked, product) => {
        try {
            const bonusItem = basket?.c_bonusProducts?.find(
                (item) => item.productId === product.productId
            )
            const updatedBasket = await updateBasket.mutateAsync({
                parameters: {basketId: basket.basketId},
                body: {
                    c_bonusProductPromoID: bonusItem?.promoId
                }
            })
            const bonusItemInBasket = updatedBasket?.productItems?.find(
                (item) => item.productId === product.productId
            )
            if (bonusItemInBasket) {
                await updateItemInBasketMutation.mutateAsync({
                    parameters: {
                        basketId: basket?.basketId,
                        itemId: bonusItemInBasket.itemId
                    },
                    body: {
                        productId: product.productId,
                        quantity: parseInt(product.quantity),
                        c_sendThisGift: checked
                    }
                })
            }
        } catch (error) {
            toast({
                title: formatMessage(API_ERROR_MESSAGE),
                status: 'error'
            })
        }
    }

    /*************************** Handle Approaching promotions ***************************/
    const approachingPromotions = useMemo(() => {
        return basket?.c_approachingBonusProductObjects?.flatMap(
            (approachingPromotionItem, index) => {
                const {bonusProductIDs, threshold} = approachingPromotionItem
                return bonusProductIDs.map((bonusProductID) => (
                    <ApproachingPromotion
                        key={`${index}-${bonusProductID}`} // Ensure a unique key for each element
                        productId={bonusProductID}
                        promotionThreshold={getConvertedPrice(threshold, conversionsRate, false)}
                        basketValue={getConvertedPrice(
                            basket?.productTotal,
                            conversionsRate,
                            false
                        )}
                        showLoading={isCartItemLoading && selectedItem?.itemId === bonusProductID}
                        currency={getConvertedCurrencyCode(conversionsRate, activeCurrency)}
                        mt={[16, null, null, 32]}
                    />
                ))
            }
        )
    }, [basket?.c_approachingBonusProductObjects])

    /***************************** Handle messages in giftBox *****************************/
    const saveProductItemGiftBoxMessage = async (product, giftBoxMessages) => {
        try {
            await updateItemInBasketMutation.mutateAsync({
                parameters: {
                    basketId: basket?.basketId,
                    itemId: product.itemId
                },
                body: {
                    productId: product.productId,
                    quantity: parseInt(product.quantity),
                    c_messages: giftBoxMessages
                }
            })
        } catch (error) {
            toast({
                title: formatMessage(API_ERROR_MESSAGE),
                status: 'error'
            })
        }
    }

    usePageLoadAndCookies('cart')

    useEffect(() => {
        /* GTM Integration For Cart View */
        if (!isProductsLoading && products) {
            triggerViewCartEvent(products)
        }
    }, [isProductsLoading])

    /********* Rendering  UI **********/
    if (isLoading) {
        return <CartSkeleton />
    }

    return (
        <>
            {/* Handles the gift card removal internally */}
            <UnavailableGiftCardModal isCartPage={true} />

            {totalItems === 0 && <EmptyCart isRegistered={isRegistered} />}

            {totalItems >= 1 && (
                <Box background="whiteSmoke" flex="1" data-testid="sf-cart-container">
                    <Container
                        maxWidth="container.xl"
                        paddingTop={{base: 24, lg: 48}}
                        paddingBottom={{base: 8, lg: 14}}
                        px="0"
                    >
                        <Stack spacing={24}>
                            <Stack spacing={24}>
                                <Box px={[10, null, null, 40]}>
                                    <CartTitle />
                                    <DisclaimerBlock
                                        page={cartPage}
                                        mt={['24px', null, null, '48px']}
                                        w={['full', null, null, 'calc(68% - 24px)']}
                                        textCenter="center"
                                        padding={['24px 8px', null, '24px']}
                                    />

                                    <HideOnDesktop>
                                        <Box ref={topCartCTARef} mt="10px">
                                            <CartCta mt="14px" />
                                        </Box>
                                    </HideOnDesktop>
                                </Box>

                                <Grid
                                    templateColumns={{base: '1fr', lg: '68% 1fr'}}
                                    gap={{base: 40, xl: 20}}
                                    mt={[8, null, null, 24]}
                                    onFocus={handleInputFocus}
                                    onBlur={handleInputBlur}
                                >
                                    <GridItem px={[10, null, null, 40]}>
                                        <Stack spacing={[16, null, null, 24]}>
                                            {basket.productItems?.map((productItem, idx) => {
                                                const model =
                                                    productsByItemId?.[productItem.itemId]
                                                        ?.c_orliweb_product_model
                                                const giftExcludedModels = MODELS_NO_GIFT

                                                if (!productItem.bonusProductLineItem) {
                                                    return (
                                                        <ProductItem
                                                            key={productItem.itemId}
                                                            index={idx}
                                                            isCart={true}
                                                            {...(productItem.itemId ===
                                                                productWithStockError?.itemId && {
                                                                hasError: true
                                                            })}
                                                            secondaryActions={
                                                                <CartSecondaryButtonGroup
                                                                    onEditClick={(product) => {
                                                                        setSelectedItem(product)
                                                                        onOpen()
                                                                    }}
                                                                    onRemoveItemClick={
                                                                        handleRemoveItem
                                                                    }
                                                                />
                                                            }
                                                            tertiaryActions={
                                                                (!model ||
                                                                    !giftExcludedModels.includes(
                                                                        model
                                                                    )) &&
                                                                // no giftbox on outlet sites
                                                                !isOutlet && (
                                                                    <GiftCheckbox
                                                                        isAGift={
                                                                            localIsGiftItems[
                                                                                productItem.itemId
                                                                            ]
                                                                                ? localIsGiftItems[
                                                                                      productItem
                                                                                          .itemId
                                                                                  ]
                                                                                : productItem.gift
                                                                        }
                                                                        onIsAGiftChange={
                                                                            handleIsAGiftChange
                                                                        }
                                                                        onMessagesChange={
                                                                            saveProductItemGiftBoxMessage
                                                                        }
                                                                    />
                                                                )
                                                            }
                                                            product={{
                                                                ...productItem,
                                                                ...(productsByItemId &&
                                                                    productsByItemId[
                                                                        productItem.itemId
                                                                    ]),
                                                                isProductUnavailable:
                                                                    !isProductsLoading
                                                                        ? !productsByItemId?.[
                                                                              productItem.itemId
                                                                          ]
                                                                        : undefined,
                                                                price: productItem.price,
                                                                quantity: productItem.quantity
                                                            }}
                                                            onItemQuantityChange={handleChangeItemQuantity.bind(
                                                                this,
                                                                productItem
                                                            )}
                                                            showLoading={
                                                                isCartItemLoading &&
                                                                selectedItem?.itemId ===
                                                                    productItem.itemId
                                                            }
                                                            handleRemoveItem={handleRemoveItem}
                                                        />
                                                    )
                                                } else {
                                                    return (
                                                        <GiftWithPurchase
                                                            key={productItem.itemId}
                                                            product={{
                                                                ...productItem,
                                                                ...(productsByItemId &&
                                                                    productsByItemId[
                                                                        productItem.itemId
                                                                    ])
                                                            }}
                                                            onIsGiftWithPurchaseChange={
                                                                handleGiftWithPurchase
                                                            }
                                                            showLoading={
                                                                isCartItemLoading &&
                                                                selectedItem?.itemId ===
                                                                    productItem.itemId
                                                            }
                                                        />
                                                    )
                                                }
                                            })}

                                            {basket.giftCertificateItems?.map(
                                                (giftCertificateItem) => {
                                                    return (
                                                        <GiftCertificateItemWithActions
                                                            key={
                                                                giftCertificateItem.giftCertificateItemId
                                                            }
                                                            giftCertificateItem={
                                                                giftCertificateItem
                                                            }
                                                            imageStyles={styles.cardImage}
                                                            headingStyles={styles.cardHeading}
                                                            {...styles.cardContainer}
                                                        />
                                                    )
                                                }
                                            )}

                                            <HideOnDesktop>{approachingPromotions}</HideOnDesktop>
                                        </Stack>
                                        <Box>
                                            {isOpen && !selectedItem?.bundledProductItems && (
                                                <ProductViewModal
                                                    isOpen={isOpen}
                                                    onOpen={onOpen}
                                                    onClose={onClose}
                                                    product={selectedItem}
                                                    showImageGalleryThumbnails
                                                    updateCart={(variant, quantity) =>
                                                        handleUpdateCart(variant, quantity)
                                                    }
                                                    shouldHideBreadcrumb={true}
                                                />
                                            )}
                                            {isOpen && selectedItem?.bundledProductItems && (
                                                <BundleProductViewModal
                                                    isOpen={isOpen}
                                                    onOpen={onOpen}
                                                    onClose={onClose}
                                                    product={selectedItem}
                                                    updateCart={(
                                                        product,
                                                        quantity,
                                                        childProducts
                                                    ) =>
                                                        handleUpdateBundle(
                                                            product,
                                                            quantity,
                                                            childProducts
                                                        )
                                                    }
                                                />
                                            )}
                                        </Box>
                                    </GridItem>
                                    <GridItem mr={[0, null, null, 40]}>
                                        <Stack
                                            spacing={4}
                                            p={'24px 20px'}
                                            bg="white"
                                            border="1px solid"
                                            borderColor={'borderGray'}
                                        >
                                            <OrderSummary
                                                showPromoCodeForm={true}
                                                showDeliveryLine={false}
                                                isEstimate={true}
                                            />
                                            <Box zIndex={2} mt={[4, null, null, 16]}>
                                                <Sticky mode="bottom" disabled={!enableStickyCTA}>
                                                    <CartCta marginTop={[6, 6, 2, 2]} mb={4} />
                                                </Sticky>
                                            </Box>
                                            <CartApplePayCta />
                                            <CartInformativeParts mt="12px" />
                                        </Stack>

                                        <HideOnMobile>{approachingPromotions}</HideOnMobile>
                                    </GridItem>
                                </Grid>

                                {/* Product Recommendations */}
                                <RecommendedProducts
                                    title={
                                        <FormattedMessage
                                            defaultMessage="LAST MINUTE ADDITIONS"
                                            id="cart.recommended_products.title.may_also_like"
                                        />
                                    }
                                    recommender={EINSTEIN_RECOMMENDERS.CART_MAY_ALSO_LIKE}
                                    products={!isProductsLoading ? Object.values(products) : []}
                                    shouldFetch={() => !isProductsLoading && products}
                                    mt={[56, null, null, 32]}
                                    mb={['42px', null, null, 80]}
                                    mx={{base: 0, sm: 0, lg: 0}}
                                    isScroller={false}
                                    isCarousel={true}
                                    nbSlidesToShow={{base: 1, md: 2, lg: 4}}
                                    maxProducts={6}
                                    titleProps={{pb: [40, null, null, 70]}}
                                />
                            </Stack>
                        </Stack>
                    </Container>
                    <ConfirmationModal
                        {...REMOVE_CART_ITEM_CONFIRMATION_DIALOG_CONFIG}
                        onPrimaryAction={() => {
                            handleRemoveItem(selectedItem)
                        }}
                        onAlternateAction={() => {}}
                        onOpen={() => {}}
                        {...modalProps}
                    />

                    <UnavailableProductConfirmationModal
                        productItems={basket?.productItems}
                        handleUnavailableProducts={handleUnavailableProducts}
                        isCart={true}
                    />
                </Box>
            )}
        </>
    )
}

export default Cart
